/**
 * ZoomControl
 * ──────────────────────────────────────────────────────────────────────
 * Floating bottom-right toolbar that lets SM / FM / mechanics adjust the
 * UI scale without touching browser settings. Works on iPad Safari and
 * desktop Chrome alike.
 *
 * iter138.9u — original request: "give a zoom in zoom out bar so sm, fm
 * and mechanics can adjust screen size as per their needs. in desktop
 * and ipad both."
 *
 * iter138.9ad — user follow-up: "FROM IPAD AND IN DESKTOP ONLY FM SM OR
 * MECHANIC CAN ZOOM OUT 80%, THEY SHOULD BE ABLE TO REDUCE EVEN UPTO
 * 40% IF THEY WANT TO ADJUST AS PER THEIR REQUIREMENT. DONT PUT LIMIT
 * FOR ZOOM IN AND OUT LET THEM DECIDE TO SET THEIR SCREEN"
 *
 * Approach:
 *   • CSS `zoom` property on document.body (Chrome / Safari / iPadOS).
 *   • Persist preference per-user-device in localStorage.
 *   • Continuous 5 % steps via −/+ buttons — NO upper or lower step
 *     gating (user decides). A SAFETY floor of 10 % is enforced solely
 *     so the zoom control itself stays clickable; otherwise no caps.
 *   • Click the percentage label to type ANY value (e.g. 33 %, 65 %,
 *     180 %) — no validation other than "must be a number ≥10".
 *   • Hidden on the public kiosk / quote / booking pages so customers
 *     can't accidentally trigger it.
 */
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const DEFAULT_ZOOM = 1.0;
const STEP = 0.05;              // 5 % per click
const FLOOR = 0.10;             // 10 % — only to keep the button clickable
const STORAGE_KEY = 'hk_ui_zoom';

// Routes where the control should NOT be shown (public-facing).
const HIDDEN_PATH_PREFIXES = [
  '/kiosk',
  '/q/',         // public quote share links
  '/book',       // public booking widget
  '/login',
  '/auth/',
];

// Clamp only against the safety floor; no upper bound — user's call.
function clampSafe(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n < FLOOR) return FLOOR;
  return Math.round(n * 100) / 100;   // snap to 2 decimals
}

export default function ZoomControl() {
  const location = useLocation();
  const [zoom, setZoom] = useState(() => {
    try {
      const saved = parseFloat(localStorage.getItem(STORAGE_KEY) || '');
      if (Number.isFinite(saved) && saved >= FLOOR) return saved;
    } catch (_e) { /* localStorage blocked — fall through */ }
    return DEFAULT_ZOOM;
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  // Apply zoom every time it changes.
  useEffect(() => {
    document.body.style.zoom = String(zoom);
    try { localStorage.setItem(STORAGE_KEY, String(zoom)); } catch (_e) { /* noop */ }
  }, [zoom]);

  // Don't show on public routes.
  const path = location.pathname;
  const hidden = HIDDEN_PATH_PREFIXES.some(p => path === p || path.startsWith(p));
  if (hidden) return null;

  const pct = Math.round(zoom * 100);

  const stepDown = () => setZoom(z => clampSafe(z - STEP));
  const stepUp = () => setZoom(z => clampSafe(z + STEP));
  const reset = () => setZoom(DEFAULT_ZOOM);

  const startEdit = () => {
    setDraft(String(pct));
    setEditing(true);
  };
  const commitEdit = () => {
    const parsed = parseFloat(draft);
    if (Number.isFinite(parsed) && parsed > 0) {
      setZoom(clampSafe(parsed / 100));
    }
    setEditing(false);
  };

  return (
    <div
      className="flex items-center gap-1 rounded-full bg-slate-800/60 backdrop-blur-sm border border-slate-700 px-1 py-1 print:hidden"
      data-testid="zoom-control"
      role="toolbar"
      aria-label="Adjust screen zoom"
    >
      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={stepDown}
        aria-label="Zoom out"
        title="Zoom out 5%"
        className="h-7 w-7 rounded-full text-slate-200 hover:bg-slate-700 hover:text-white"
        data-testid="zoom-out-btn"
      >
        <ZoomOut className="h-3.5 w-3.5" />
      </Button>

      {editing ? (
        <input
          type="number"
          inputMode="numeric"
          min={Math.round(FLOOR * 100)}
          step={1}
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitEdit();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="w-12 bg-slate-900 text-slate-100 text-[11px] font-medium tabular-nums rounded px-1 py-0.5 border border-slate-600 focus:outline-none focus:border-emerald-400 text-center"
          aria-label="Type a zoom percentage"
          data-testid="zoom-input"
        />
      ) : (
        <button
          type="button"
          onClick={startEdit}
          className="text-[11px] font-medium text-slate-200 hover:text-white px-1.5 min-w-[2.75rem] tabular-nums"
          title="Click to type any zoom % (no limits)"
          aria-label={`Current zoom ${pct} percent — click to type any value`}
          data-testid="zoom-reset-btn"
        >
          {pct}%
        </button>
      )}

      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={stepUp}
        aria-label="Zoom in"
        title="Zoom in 5%"
        className="h-7 w-7 rounded-full text-slate-200 hover:bg-slate-700 hover:text-white"
        data-testid="zoom-in-btn"
      >
        <ZoomIn className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={reset}
        aria-label="Reset zoom to 100 percent"
        title="Reset zoom to 100%"
        className="h-7 w-7 rounded-full text-slate-200 hover:bg-slate-700 hover:text-white"
        data-testid="zoom-default-btn"
      >
        <Maximize2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
